/* ⚠️ GENERATED from app/services/profileFields.ts by gen-twins.mjs — edit the TypeScript, not this. */
// services/profileFields.ts — the optional things a profile can say about
// itself, and who is allowed to read each one.
//
// ══ WHAT THIS IS FOR ═══════════════════════════════════════════════════════
// A page for a public figure wants to say more than a shop does: the pronouns
// they go by, the city they live in, how far they took their education, and
// where to find them on the other networks. A person's own profile wants the
// same list, optionally. And every one of those answers is something its owner
// may want on the profile or may want kept to themselves.
//
// 🟥 SO THE FIELD AND THE PERMISSION ARE ONE THING, NOT TWO. Adding a field in
//    one place and remembering to add its visibility somewhere else is how a
//    profile ends up publishing an answer nobody agreed to publish. A field
//    that is not in this table does not exist; a field that is in it always has
//    an answer to "who can see this".
//
// 🟥 AND "HIDDEN" MEANS UNREADABLE, NOT UNDRAWN. Both clients read profile
//    documents straight out of Firestore, so a value left on the document and
//    merely skipped by the renderer is still one console command away from
//    anybody signed in. Hidden answers are stored somewhere else entirely —
//    see `profileStore.ts` — and this module never sees them unless the owner
//    is the one asking. That is the whole reason the split exists.
//
// 🟢 ONE TABLE, EVERY SURFACE. The page wizard builds its questions from it,
//    the page profile draws its facts from it, the personal profile edits from
//    it, and the web client uses the generated twin of this exact file. A field
//    added here appears in all six places without any of them being edited.

/** How a field is answered, which decides what the editor draws. */

/* 🟢 THE ORDER IS THE ORDER THEY ARE ASKED AND THE ORDER THEY ARE SHOWN. Who
   you are, then where you are, then what you studied, then where else to find
   you. One list, so an editor and a profile can never disagree about it. */
export const PROFILE_FIELDS = [{
  key: 'pronouns',
  input: 'choice',
  icon: 'person-outline',
  options: ['he_him', 'she_her', 'they_them', 'prefer_not']
}, {
  key: 'city',
  input: 'text',
  icon: 'home-outline'
}, {
  key: 'education',
  input: 'choice',
  icon: 'school-outline',
  options: ['school', 'college', 'bachelor', 'master', 'doctorate', 'other']
}, {
  key: 'facebook',
  input: 'handle',
  icon: 'logo-facebook',
  base: 'https://facebook.com/',
  reach: true
}, {
  key: 'instagram',
  input: 'handle',
  icon: 'logo-instagram',
  base: 'https://instagram.com/',
  reach: true
}, {
  key: 'tiktok',
  input: 'handle',
  icon: 'logo-tiktok',
  base: 'https://tiktok.com/@',
  reach: true
}, {
  key: 'x',
  input: 'handle',
  icon: 'logo-twitter',
  base: 'https://x.com/',
  reach: true
}, {
  key: 'youtube',
  input: 'handle',
  icon: 'logo-youtube',
  base: 'https://youtube.com/@',
  reach: true
}];
export const FIELD_KEYS = PROFILE_FIELDS.map(f => f.key);
export const fieldByKey = k => PROFILE_FIELDS.find(f => f.key === k) || null;

/**
 * A handle typed any of the ways people type one, turned into a link.
 *
 * 🟥 PEOPLE PASTE THE WHOLE URL, and they type a bare name, and they type it
 *    with an @ in front. All three mean the same account and all three have to
 *    open it. Storing whatever was typed and hoping is how a profile ends up
 *    with a link to `https://tiktok.com/@https://tiktok.com/@someone`.
 *
 * 🟢 WHAT IS STORED IS WHAT WAS TYPED. This runs when the link is opened, not
 *    when it is saved, so a value is never rewritten under its owner and a
 *    fixed parser here repairs every profile at once rather than only the ones
 *    edited since.
 */
export function fieldLink(key, value) {
  const f = fieldByKey(key);
  const raw = String(value || '').trim();
  if (!f || !f.base || !raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  /* a bare domain, which is a URL missing only its scheme */
  if (/^[a-z0-9.-]+\.[a-z]{2,}\//i.test(raw)) return `https://${raw}`;
  const handle = raw.replace(/^@+/, '').replace(/^\/+/, '');
  return handle ? `${f.base}${handle}` : null;
}

/* ═══ WHO CAN READ IT ══════════════════════════════════════════════════════

   🟢 THERE IS NO VISIBILITY FLAG, AND THAT IS THE DESIGN. A second map saying
      `{ city: 'hidden' }` beside a map saying `{ city: 'Amman' }` is two facts
      that can disagree, and the way they disagree is always the same way: the
      flag says hidden and the value is still sitting on a document anybody can
      read. So WHERE the answer is stored IS the permission. A value in the
      profile document is public because it is in the public document; a value
      in the owner-only document is hidden because nobody else may open it.

   🟥 WHICH MEANS A HIDDEN VALUE NEVER REACHES A VIEWER'S DEVICE. Not styled
      out, not filtered in the renderer, not omitted by a projection somebody
      could forget: not fetched, because the fetch would be refused. Every
      function below that takes a private map is therefore an OWNER function,
      and the ones a viewer's screen calls cannot be handed one.                */

/** Answers, by key. Both the public map and the owner-only map are this shape. */

/** What the owner sees when editing: the answer, and who can read it. */

const clean = m => {
  const out = {};
  if (!m || typeof m !== 'object') return out;
  for (const k of Object.keys(m)) {
    const v = m[k];
    if (typeof v === 'string' && v.trim()) out[k] = v.trim();
  }
  return out;
};

/**
 * Everything the owner has answered, public and hidden together.
 *
 * 🟥 THE PUBLIC MAP WINS A COLLISION, and the collision must not be silent to
 *    the code that repairs it. A key in both places means an earlier save was
 *    interrupted between its two writes; treating it as public is the safe way
 *    round, because the value is already readable and pretending otherwise
 *    would tell the owner it is private when it is not.
 */
export function ownerFields(pub, priv) {
  const P = clean(pub);
  const H = clean(priv);
  const keys = [];
  for (const f of PROFILE_FIELDS) if (P[f.key] || H[f.key]) keys.push(f.key);
  for (const k of [...Object.keys(P), ...Object.keys(H)]) if (!keys.includes(k)) keys.push(k);
  return keys.map(key => P[key] ? {
    key,
    value: P[key],
    visibility: 'public'
  } : {
    key,
    value: H[key],
    visibility: 'hidden'
  });
}

/**
 * What a visitor sees. Takes the public map ONLY, on purpose: there is no
 * argument to this function that could carry a hidden answer into a view.
 */
export function publicFields(pub) {
  const P = clean(pub);
  const keys = PROFILE_FIELDS.map(f => f.key).filter(k => P[k]);
  for (const k of Object.keys(P)) if (!keys.includes(k)) keys.push(k);
  return keys.map(key => ({
    key,
    value: P[key],
    visibility: 'public'
  }));
}

/** A key that has been answered but kept back, for the owner's own editor. */
export const isHidden = (key, pub, priv) => !clean(pub)[key] && !!clean(priv)[key];

/* ═══ WHERE A PUBLIC ANSWER LIVES ══════════════════════════════════════════

   🟥 A PAGE ALREADY HAD THREE HOMES BEFORE THIS EXISTED. `website`, `location`,
      `hours` and `phone` are top-level fields on the page document because the
      profile screen and the feed have always read them there; the wizard's
      other answers go into an `extra` map; and the fields added here go into
      `profileFields`. Nothing is gained by moving the old ones, and a migration
      of every page in the product is a real risk taken for tidiness.

   🟢 SO THE ROUTING IS STATED ONCE, HERE, and the save path asks rather than
      assumes. Hiding `website` has to clear the top-level field, not just add a
      copy somewhere else, or the answer stays readable and the owner is told it
      is not.                                                                   */

/** The four page fields that predate this module and stay where they are. */
export const PAGE_ROOT_KEYS = ['website', 'location', 'hours', 'phone'];
export function homeOf(key, kind) {
  if (FIELD_KEYS.includes(key)) return 'fields';
  if (kind === 'page' && PAGE_ROOT_KEYS.includes(key)) return 'root';
  return 'extra';
}

/**
 * Everything on a page that can be shown or hidden, in the order it is drawn.
 *
 * 🟢 "EVERYTHING SHOWING IN THE PAGE PROFILE" WAS THE ASK, so this is not the
 *    new fields only: the website, the address, the opening hours and the phone
 *    number are answers somebody gave too, and each is a thing a public figure
 *    might want off their page without deleting it.
 */
export function pageFieldOrder(extraKeys) {
  const out = [];
  for (const f of PROFILE_FIELDS) out.push(f.key);
  for (const k of PAGE_ROOT_KEYS) out.push(k);
  for (const k of extraKeys) if (!out.includes(k)) out.push(k);
  return out;
}

/* ═══ WHICH PAGES ARE A PERSON ═════════════════════════════════════════════

   🟥 "THIS PAGE MIGHT BE A CELEBRITY, OR SOMEONE FAMOUS" — and a restaurant is
      not, which is the whole distinction. Pronouns and a hometown are sensible
      questions for a page that IS a person and faintly absurd for one that is a
      shop, so the questions follow the category rather than being asked of
      everybody in the name of consistency.

   🟢 AND THE DECISION LIVES HERE, not in the phone's category file, because the
      browser's create-page form has to ask exactly the same things. It used to
      be possible for the two clients to offer different questions for the same
      category, and nothing would have said so.

   🟢 'links' RATHER THAN 'all' FOR A SPORTS PAGE. A club has a TikTok and does
      not have an education. Same table, one word different.                    */

export const IDENTITY_CATEGORIES = {
  public_figure: 'all',
  /* somebody famous who picked one of these before the category existed should
     not have to move their page to be asked the same things */
  creator: 'all',
  entertainment: 'all',
  sport: 'links'
};

/** The identity questions this category is asked, in order. */
export function identityFor(category) {
  const depth = IDENTITY_CATEGORIES[String(category || '')];
  if (!depth) return [];
  return PROFILE_FIELDS.filter(f => depth === 'all' || !!f.reach).map(f => f.key);
}

/* ═══ WORDS ════════════════════════════════════════════════════════════════
   🟢 THE SAME TEN LANGUAGES THE REST OF PAGES CARRIES, embedded rather than
      spread across ten locale files, exactly as `pageCategories.tsx` does it.
      A field and its wording arrive together or the field arrives half done.  */

const FIELD_LABELS = {
  pronouns: {
    en: 'Pronouns',
    es: 'Pronombres',
    fr: 'Pronoms',
    de: 'Pronomen',
    zh: '人称代词',
    ja: '代名詞',
    ru: 'Местоимения',
    ar: 'الضمائر',
    pt: 'Pronomes',
    hi: 'सर्वनाम'
  },
  city: {
    en: 'Lives in',
    es: 'Vive en',
    fr: 'Habite à',
    de: 'Wohnt in',
    zh: '现居',
    ja: '居住地',
    ru: 'Живёт в',
    ar: 'يقيم في',
    pt: 'Mora em',
    hi: 'निवास'
  },
  education: {
    en: 'Education',
    es: 'Educación',
    fr: 'Études',
    de: 'Ausbildung',
    zh: '学历',
    ja: '学歴',
    ru: 'Образование',
    ar: 'التعليم',
    pt: 'Formação',
    hi: 'शिक्षा'
  },
  facebook: {
    en: 'Facebook',
    es: 'Facebook',
    fr: 'Facebook',
    de: 'Facebook',
    zh: 'Facebook',
    ja: 'Facebook',
    ru: 'Facebook',
    ar: 'فيسبوك',
    pt: 'Facebook',
    hi: 'फेसबुक'
  },
  instagram: {
    en: 'Instagram',
    es: 'Instagram',
    fr: 'Instagram',
    de: 'Instagram',
    zh: 'Instagram',
    ja: 'Instagram',
    ru: 'Instagram',
    ar: 'إنستغرام',
    pt: 'Instagram',
    hi: 'इंस्टाग्राम'
  },
  tiktok: {
    en: 'TikTok',
    es: 'TikTok',
    fr: 'TikTok',
    de: 'TikTok',
    zh: 'TikTok',
    ja: 'TikTok',
    ru: 'TikTok',
    ar: 'تيك توك',
    pt: 'TikTok',
    hi: 'टिकटॉक'
  },
  x: {
    en: 'X',
    es: 'X',
    fr: 'X',
    de: 'X',
    zh: 'X',
    ja: 'X',
    ru: 'X',
    ar: 'إكس',
    pt: 'X',
    hi: 'X'
  },
  youtube: {
    en: 'YouTube',
    es: 'YouTube',
    fr: 'YouTube',
    de: 'YouTube',
    zh: 'YouTube',
    ja: 'YouTube',
    ru: 'YouTube',
    ar: 'يوتيوب',
    pt: 'YouTube',
    hi: 'यूट्यूब'
  }
};
const OPTION_LABELS = {
  he_him: {
    en: 'He / him',
    es: 'Él',
    fr: 'Il',
    de: 'Er',
    zh: '他',
    ja: '彼',
    ru: 'Он',
    ar: 'هو',
    pt: 'Ele',
    hi: 'वह (पुरुष)'
  },
  she_her: {
    en: 'She / her',
    es: 'Ella',
    fr: 'Elle',
    de: 'Sie',
    zh: '她',
    ja: '彼女',
    ru: 'Она',
    ar: 'هي',
    pt: 'Ela',
    hi: 'वह (महिला)'
  },
  they_them: {
    en: 'They / them',
    es: 'Elle',
    fr: 'Iel',
    de: 'They',
    zh: 'TA',
    ja: 'その他',
    ru: 'Они',
    ar: 'هم',
    pt: 'Elu',
    hi: 'वे'
  },
  prefer_not: {
    en: 'Rather not say',
    es: 'Prefiero no decirlo',
    fr: 'Je préfère ne pas dire',
    de: 'Keine Angabe',
    zh: '不愿透露',
    ja: '回答しない',
    ru: 'Не указывать',
    ar: 'أفضل عدم الإفصاح',
    pt: 'Prefiro não dizer',
    hi: 'नहीं बताना चाहते'
  },
  school: {
    en: 'High school',
    es: 'Bachillerato',
    fr: 'Lycée',
    de: 'Schule',
    zh: '高中',
    ja: '高校',
    ru: 'Школа',
    ar: 'الثانوية',
    pt: 'Ensino médio',
    hi: 'हाई स्कूल'
  },
  college: {
    en: 'College',
    es: 'Instituto técnico',
    fr: 'Études courtes',
    de: 'Fachschule',
    zh: '大专',
    ja: '専門学校',
    ru: 'Колледж',
    ar: 'كلية متوسطة',
    pt: 'Curso técnico',
    hi: 'कॉलेज'
  },
  bachelor: {
    en: 'Bachelor',
    es: 'Licenciatura',
    fr: 'Licence',
    de: 'Bachelor',
    zh: '学士',
    ja: '学士',
    ru: 'Бакалавр',
    ar: 'بكالوريوس',
    pt: 'Graduação',
    hi: 'स्नातक'
  },
  master: {
    en: 'Master',
    es: 'Máster',
    fr: 'Master',
    de: 'Master',
    zh: '硕士',
    ja: '修士',
    ru: 'Магистр',
    ar: 'ماجستير',
    pt: 'Mestrado',
    hi: 'स्नातकोत्तर'
  },
  doctorate: {
    en: 'Doctorate',
    es: 'Doctorado',
    fr: 'Doctorat',
    de: 'Doktor',
    zh: '博士',
    ja: '博士',
    ru: 'Докторская',
    ar: 'دكتوراه',
    pt: 'Doutorado',
    hi: 'पीएचडी'
  },
  other: {
    en: 'Other',
    es: 'Otro',
    fr: 'Autre',
    de: 'Sonstiges',
    zh: '其他',
    ja: 'その他',
    ru: 'Другое',
    ar: 'أخرى',
    pt: 'Outro',
    hi: 'अन्य'
  }
};
const UI = {
  visible: {
    en: 'Visible',
    es: 'Visible',
    fr: 'Visible',
    de: 'Sichtbar',
    zh: '公开',
    ja: '公開',
    ru: 'Видно всем',
    ar: 'ظاهر',
    pt: 'Visível',
    hi: 'सबको दिखे'
  },
  hidden: {
    en: 'Hidden',
    es: 'Oculto',
    fr: 'Masqué',
    de: 'Verborgen',
    zh: '隐藏',
    ja: '非公開',
    ru: 'Скрыто',
    ar: 'مخفي',
    pt: 'Oculto',
    hi: 'छिपा'
  },
  only_you: {
    en: 'Only you can see this',
    es: 'Solo tú puedes verlo',
    fr: 'Vous seul pouvez le voir',
    de: 'Nur du siehst das',
    zh: '仅自己可见',
    ja: '自分だけが見られます',
    ru: 'Видно только вам',
    ar: 'أنت وحدك تراه',
    pt: 'Só você vê isto',
    hi: 'केवल आप देख सकते हैं'
  },
  about_you: {
    en: 'About you',
    es: 'Sobre ti',
    fr: 'À propos de vous',
    de: 'Über dich',
    zh: '关于你',
    ja: 'あなたについて',
    ru: 'О вас',
    ar: 'عنك',
    pt: 'Sobre você',
    hi: 'आपके बारे में'
  },
  add_details: {
    en: 'Add details',
    es: 'Añadir datos',
    fr: 'Ajouter des infos',
    de: 'Angaben hinzufügen',
    zh: '添加资料',
    ja: '情報を追加',
    ru: 'Добавить сведения',
    ar: 'إضافة تفاصيل',
    pt: 'Adicionar dados',
    hi: 'जानकारी जोड़ें'
  },
  details: {
    en: 'Details',
    es: 'Datos',
    fr: 'Informations',
    de: 'Angaben',
    zh: '资料',
    ja: '詳細',
    ru: 'Сведения',
    ar: 'التفاصيل',
    pt: 'Dados',
    hi: 'जानकारी'
  },
  find_me: {
    en: 'Find me on',
    es: 'Encuéntrame en',
    fr: 'Retrouvez-moi sur',
    de: 'Finde mich auf',
    zh: '其他平台',
    ja: '他のSNS',
    ru: 'Я в других сетях',
    ar: 'تجدني على',
    pt: 'Me encontre em',
    hi: 'अन्य जगह'
  },
  nothing_yet: {
    en: 'Nothing added yet',
    es: 'Aún no hay nada',
    fr: 'Rien pour le moment',
    de: 'Noch nichts',
    zh: '暂无内容',
    ja: 'まだありません',
    ru: 'Пока ничего',
    ar: 'لا شيء بعد',
    pt: 'Nada ainda',
    hi: 'अभी कुछ नहीं'
  },
  all_optional: {
    en: 'All of this is optional',
    es: 'Todo esto es opcional',
    fr: 'Tout ceci est facultatif',
    de: 'Alles davon ist optional',
    zh: '以下内容均为可选',
    ja: 'すべて任意です',
    ru: 'Всё это необязательно',
    ar: 'كل هذا اختياري',
    pt: 'Tudo isto é opcional',
    hi: 'यह सब वैकल्पिक है'
  },
  save: {
    en: 'Save',
    es: 'Guardar',
    fr: 'Enregistrer',
    de: 'Speichern',
    zh: '保存',
    ja: '保存',
    ru: 'Сохранить',
    ar: 'حفظ',
    pt: 'Salvar',
    hi: 'सहेजें'
  },
  saving: {
    en: 'Saving',
    es: 'Guardando',
    fr: 'Enregistrement',
    de: 'Wird gespeichert',
    zh: '保存中',
    ja: '保存中',
    ru: 'Сохранение',
    ar: 'جارٍ الحفظ',
    pt: 'Salvando',
    hi: 'सहेजा जा रहा है'
  },
  save_failed: {
    en: 'That did not save. Please try again.',
    es: 'No se guardó. Inténtalo de nuevo.',
    fr: "Cela n'a pas été enregistré. Réessayez.",
    de: 'Das wurde nicht gespeichert. Bitte erneut versuchen.',
    zh: '保存失败，请重试。',
    ja: '保存できませんでした。もう一度お試しください。',
    ru: 'Не сохранилось. Попробуйте ещё раз.',
    ar: 'لم يتم الحفظ. حاول مرة أخرى.',
    pt: 'Não foi salvo. Tente novamente.',
    hi: 'सहेजा नहीं गया। फिर से आज़माएं।'
  },
  fr_who: {
    en: 'Friend requests',
    es: 'Solicitudes de amistad',
    fr: "Demandes d'ami",
    de: 'Freundschaftsanfragen',
    zh: '好友请求',
    ja: '友達リクエスト',
    ru: 'Заявки в друзья',
    ar: 'طلبات الصداقة',
    pt: 'Pedidos de amizade',
    hi: 'मित्रता अनुरोध'
  },
  fr_anyone: {
    en: 'Anyone can send one',
    es: 'Cualquiera puede enviarla',
    fr: "N'importe qui peut en envoyer",
    de: 'Jeder kann eine senden',
    zh: '任何人都可以发送',
    ja: '誰でも送れます',
    ru: 'Любой может отправить',
    ar: 'يمكن لأي شخص إرسالها',
    pt: 'Qualquer um pode enviar',
    hi: 'कोई भी भेज सकता है'
  },
  fr_noone: {
    en: 'No one can send one',
    es: 'Nadie puede enviarla',
    fr: 'Personne ne peut en envoyer',
    de: 'Niemand kann eine senden',
    zh: '任何人都不能发送',
    ja: '誰も送れません',
    ru: 'Никто не может отправить',
    ar: 'لا أحد يمكنه إرسالها',
    pt: 'Ninguém pode enviar',
    hi: 'कोई नहीं भेज सकता'
  },
  fr_off: {
    en: 'Requests are off',
    es: 'Solicitudes desactivadas',
    fr: 'Demandes désactivées',
    de: 'Anfragen aus',
    zh: '已关闭请求',
    ja: 'リクエスト停止中',
    ru: 'Заявки отключены',
    ar: 'الطلبات موقوفة',
    pt: 'Pedidos desativados',
    hi: 'अनुरोध बंद हैं'
  },
  fr_off_note: {
    en: 'They are not accepting friend requests',
    es: 'No acepta solicitudes de amistad',
    fr: "Cette personne n'accepte pas les demandes d'ami",
    de: 'Nimmt keine Freundschaftsanfragen an',
    zh: '对方不接受好友请求',
    ja: '友達リクエストを受け付けていません',
    ru: 'Не принимает заявки в друзья',
    ar: 'لا يقبل طلبات الصداقة',
    pt: 'Não aceita pedidos de amizade',
    hi: 'मित्रता अनुरोध स्वीकार नहीं कर रहे'
  }
};
const pick = (row, lang, fallback) => {
  if (!row) return fallback || '';
  const l = String(lang || 'en').slice(0, 2);
  return row[l] || row.en || fallback || '';
};
export const fieldLabel = (key, lang) => pick(FIELD_LABELS[key], lang, key);
export const optionLabel = (id, lang) => pick(OPTION_LABELS[id], lang, id);
export const fieldsL = (key, lang) => pick(UI[key], lang, key);

/** A saved answer as it should read on a profile. */
export function fieldDisplay(key, value, lang) {
  const f = fieldByKey(key);
  const v = String(value || '').trim();
  if (!v) return '';
  if (f?.input === 'choice') return optionLabel(v, lang);
  if (f?.input === 'handle') return v.replace(/^https?:\/\/(www\.)?[^/]+\/@?/i, '').replace(/^@+/, '') || v;
  return v;
}
